package com.vizio.redwolf.pay.api

import com.vizio.redwolf.pay.client.OffersHttpClient
import com.vizio.redwolf.pay.model.OffersFailureResponse
import com.vizio.redwolf.pay.model.OfferDetailsByOfferIdRequestInfo
import com.vizio.redwolf.pay.model.OfferDetailsByOfferIdResponse
import com.vizio.redwolf.pay.model.OfferIdByLinkingPinClientRequestInfo
import com.vizio.redwolf.pay.model.OfferIdByLinkingPinRequestInfo
import com.vizio.redwolf.pay.model.OfferIdByLinkingPinResponse
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.OffersEnvironment
import com.vizio.redwolf.utils.OffersEnvironmentConfig
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.chooseOffersEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.http.isSuccess
import kotlin.js.JsExport

class OffersApi(
    environment: OffersEnvironment,
    private val appConfig: AppConfig,
    private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)
) :
    OffersApiInterface {

    private val offersClient = OffersHttpClient(environment, appConfig, clientConfig)

    constructor(environmentConfig: OffersEnvironmentConfig, appConfig: AppConfig) :
        this(chooseOffersEnvironment(environmentConfig), appConfig)

    constructor(environmentConfig: OffersEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) :
        this(chooseOffersEnvironment(environmentConfig), appConfig, clientConfig)

    constructor(environmentConfig: OffersEnvironmentConfig, appConfig: AppConfig, timeout: Long) :
        this(chooseOffersEnvironment(environmentConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS, timeout))

    override suspend fun requestOfferDetailsByOfferId(requestInfo: OfferDetailsByOfferIdRequestInfo, traceId: String?): OffersResult<OfferDetailsByOfferIdResponse> {
        return retryOffersRequest {
            val result = offersClient.requestOfferDetailsByOfferId(requestInfo, traceId)
            handleOffersHttpResult(result)
        }
    }

    override suspend fun requestOfferIdByLinkingPin(requestInfo: OfferIdByLinkingPinRequestInfo, traceId: String?): OffersResult<OfferIdByLinkingPinResponse> {
        val clientRequestInfo = OfferIdByLinkingPinClientRequestInfo(
            linkingPin = requestInfo.linkingPin,
            deviceInstanceId = requestInfo.deviceInstanceId
        )
        return retryOffersRequest {
            val result = offersClient.requestOfferIdByLinkingPin(requestInfo.m2mToken, clientRequestInfo, traceId)
            handleOffersHttpResult(result)
        }
    }

    private suspend inline fun <T : HttpResponse, reified R : Any> handleOffersHttpResult(httpResult: HttpResult<T>): OffersResult<R> {
        return when (httpResult) {
            is HttpResult.Success -> {
                val httpResponse: HttpResponse = httpResult.data
                val statusCode = httpResponse.status.value
                val headers = ResponseHeaders.Valid(statusCode)
                if (httpResponse.status.isSuccess()) {
                    try {
                        OffersResult.Success(headers, httpResponse.body())
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        OffersResult.Error(headers, ex)
                    }
                } else {
                    try {
                        val failureMessage = httpResponse.body<OffersFailureResponse>()
                        OffersResult.Failed(headers, response = failureMessage)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        OffersResult.Error(headers, ex)
                    }
                }
            }

            is HttpResult.Error -> {
                val headers = ResponseHeaders.Invalid
                val exception = httpResult.exception
                if (exception is TimeoutException) {
                    OffersResult.Timeout(headers, exception)
                } else {
                    OffersResult.Error(ResponseHeaders.Invalid, exception)
                }
            }
        }
    }

    private fun <T : Any> isOffersSuccess(result: T): Boolean {
        return result is OffersResult<*>
    }

    private suspend inline fun <T : Any> retryOffersRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isOffersSuccess)
    }
}

@JsExport
sealed class OffersResult<out T : Any> {
    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : OffersResult<T>()
    data class Failed(val headers: ResponseHeaders, val response: OffersFailureResponse) : OffersResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : OffersResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : OffersResult<Nothing>()

    fun <R> fold(
        onSuccess: (success: Success<T>) -> R,
        onFailed: (failed: Failed) -> R,
        onError: (error: Error) -> R,
        onTimeout: (timeout: Timeout) -> R
    ): R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is Failed -> onFailed(Failed(this.headers, this.response))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}